/**
 * Share one exam with the patient it belongs to.
 *
 * Built on the DeepEcho components in ComponnentsUI/common rather than antd. Not
 * only for consistency: this app enables Tailwind preflight with no antd reset,
 * and antd v5 styles its controls through `:where()` selectors that carry zero
 * specificity, so preflight wins and its checkboxes and switches render as blank
 * white shapes. The house components are plain elements and do not have that
 * problem.
 *
 * Three decisions are locked and deliberately not offered as choices:
 *
 * - the link is single use, so there is no "how many opens" control
 * - the channel is one of three, so there is no bare copy-a-URL option
 * - the challenge is picked by the server from what the record can support, so
 *   the dialog reports it rather than setting it
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import QRCode from 'react-qr-code';
import {
  LuAlertTriangle,
  LuCheck,
  LuCopy,
  LuMail,
  LuMessageCircle,
  LuPrinter,
  LuQrCode,
  LuX,
} from 'react-icons/lu';

import { Button } from '../../ComponnentsUI/common/Button';
import { Checkbox } from '../../ComponnentsUI/common/Checkbox';
import { Input } from '../../ComponnentsUI/common/Input';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Select } from '../../ComponnentsUI/common/Select';
import {
  useCreateExamShare,
  useExamShares,
  useRevokeExamShare,
  useShareRecipient,
  type ExamShare,
  type ExamShareCreated,
  type ShareChallengeField,
  type ShareContentScope,
  type ShareDeliveryChannel,
  type ShareRedactionProfile,
} from '../../../helpers/api/reports/exam-share';

interface ShareDialogProps {
  open: boolean;
  studyId: string;
  onClose: () => void;
}

const TTL_CHOICES = [6, 12, 24, 48, 72];

// Elements rather than component references, and the icon type left to
// inference: react-icons will not go behind a React.ComponentType alias, and the
// repo has two copies of @types/react, so annotating this React.ReactNode picks
// the wrong one and stops matching what JSX here actually produces.
const CHANNELS = [
  { value: 'qr_code' as ShareDeliveryChannel, icon: <LuQrCode className="h-5 w-5" /> },
  { value: 'whatsapp' as ShareDeliveryChannel, icon: <LuMessageCircle className="h-5 w-5" /> },
  { value: 'email' as ShareDeliveryChannel, icon: <LuMail className="h-5 w-5" /> },
];

const SCOPES: ShareContentScope[] = ['both', 'clinical_only', 'images_only'];

/** Mirrors the server's normalize_phone so the dialog rejects what the send would
 *  have rejected anyway, before a link is minted and left undelivered. */
function normalizePhone(raw: string): string | null {
  let digits = raw.trim().replace(/[^\d+]/g, '');
  if (digits.startsWith('00')) {
    digits = `+${digits.slice(2)}`;
  }
  if (!digits.startsWith('+')) {
    return null;
  }
  const bare = digits.slice(1);
  return /^\d{8,15}$/.test(bare) ? `+${bare}` : null;
}

/** Mirrors EmailChannel._normalize_email. Deliberately not a validity check:
 *  RFC-correct addresses get refused by real mail servers all the time. */
function normalizeEmail(raw: string): string | null {
  const candidate = raw.trim();
  if (!candidate || /\s/.test(candidate)) {
    return null;
  }
  const [local, domain, ...rest] = candidate.split('@');
  if (!local || !domain || rest.length || !domain.includes('.')) {
    return null;
  }
  return candidate;
}

function formatWhen(value: string | null, locale: string): string {
  if (!value) {
    return '-';
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Why a share can no longer be opened. Mirrors the server's state machine. */
function shareStatusKey(share: ExamShare): string {
  if (share.revoked_at) {
    return 'share.statusRevoked';
  }
  if (new Date(share.expires_at).getTime() <= Date.now()) {
    return 'share.statusExpired';
  }
  if (share.unlock_count >= share.max_unlocks) {
    return 'share.statusUsedUp';
  }
  return 'share.statusActive';
}

const sectionLabel =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400';

export function ShareDialog({ open, studyId, onClose }: ShareDialogProps) {
  const { t, i18n } = useTranslation('Report');
  const locale = i18n.resolvedLanguage || i18n.language || 'en-US';

  const [channel, setChannel] = useState<ShareDeliveryChannel>('qr_code');
  const [scope, setScope] = useState<ShareContentScope>('both');
  const [ttlHours, setTtlHours] = useState(12);
  const [anonymize, setAnonymize] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [minted, setMinted] = useState<ExamShareCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement | null>(null);

  const { data: shares, isLoading: sharesLoading } = useExamShares(studyId);
  const revoke = useRevokeExamShare(studyId);
  const create = useCreateExamShare(studyId, created => {
    setMinted(created);
    setCopied(false);
  });

  // Fetched whenever the dialog is open, not just for the sending channels: the
  // challenge it carries has to be shown before a link is minted, and that is
  // what the clinician reads out to the patient even for a printed code.
  const { data: recipient, isFetching: recipientLoading } = useShareRecipient(
    studyId,
    open
  );

  // Prefill once per field, then leave it alone so a correction survives a
  // refetch.
  useEffect(() => {
    if (touched) {
      return;
    }
    if (recipient?.phone_number) {
      setPhone(recipient.phone_number);
    }
    if (recipient?.email) {
      setEmail(recipient.email);
    }
  }, [touched, recipient?.phone_number, recipient?.email]);

  const profile: ShareRedactionProfile = anonymize ? 'anonymized' : 'identified';

  const needsPhone = channel === 'whatsapp';
  const needsEmail = channel === 'email';
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);
  const normalizedEmail = useMemo(() => normalizeEmail(email), [email]);

  const destinationOk =
    (!needsPhone || !!normalizedPhone) && (!needsEmail || !!normalizedEmail);
  const destinationTyped = needsPhone
    ? phone.trim().length > 0
    : needsEmail
      ? email.trim().length > 0
      : false;
  const destinationInvalid = destinationTyped && !destinationOk;
  const onFile = needsPhone ? !!recipient?.phone_number : !!recipient?.email;

  // A record with no date of birth cannot be challenged, so the server will
  // refuse. Saying so here beats letting them fill the form in first.
  const challengeFields: ShareChallengeField[] = recipient?.challenge?.fields ?? [];
  const cannotChallenge =
    !!recipient && !recipientLoading && challengeFields.length === 0;

  const canSubmit =
    !create.isPending && !recipientLoading && !cannotChallenge && destinationOk;

  const reset = useCallback(() => {
    setMinted(null);
    setCopied(false);
    setChannel('qr_code');
    setScope('both');
    setTtlHours(12);
    setAnonymize(false);
    setTouched(false);
    setPhone('');
    setEmail('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleCreate = useCallback(() => {
    create.mutate({
      content_scope: scope,
      redaction_profile: profile,
      ttl_hours: ttlHours,
      delivery_channel: channel,
      ...(needsPhone && normalizedPhone ? { recipient_phone: normalizedPhone } : {}),
      ...(needsEmail && normalizedEmail ? { recipient_email: normalizedEmail } : {}),
    });
  }, [
    create,
    scope,
    profile,
    ttlHours,
    channel,
    needsPhone,
    needsEmail,
    normalizedPhone,
    normalizedEmail,
  ]);

  const handleCopy = useCallback(async () => {
    if (!minted?.url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(minted.url);
      setCopied(true);
      message.success(t('share.copied'));
    } catch {
      message.error(t('share.copyFailed'));
    }
  }, [minted, t]);

  /** Print the code on its own sheet.
   *
   *  Through a hidden iframe rather than a popup: window.open is what ad blockers
   *  and browser settings suppress, and calling window.print() directly here
   *  would print the whole application behind the modal. */
  const handlePrint = useCallback(() => {
    const svg = qrRef.current?.innerHTML;
    if (!svg) {
      return;
    }

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;left:-9999px';
    document.body.appendChild(frame);

    const doc = frame.contentDocument;
    if (!doc) {
      frame.remove();
      return;
    }

    // Second person throughout: this sheet is read by the patient, not by the
    // clinician who printed it. It carries the instructions and nothing that
    // would identify her to whoever finds it on a waiting room floor.
    const asked = t(
      `share.challengeNamesYou.${minted?.challenge?.policy ?? 'dob'}`
    );

    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${t(
        'share.printTitle'
      )}</title><style>
        /* No page margin, which is where Chrome prints the date, the page
           title, the URL and "1/1". A handout for a patient should not carry
           the clinician's localhost address across the bottom. The padding
           below replaces the margin we just gave up. */
        @page { size: A4; margin: 0 }

        * { box-sizing: border-box }
        html, body { margin: 0; padding: 0 }
        body {
          font-family: Arial, Helvetica, sans-serif;
          color: #0f172a;
          /* Background panels and the header bar are the design, not decoration,
             so make sure they survive the print pipeline. */
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .sheet { padding: 0 0 28mm }

        .masthead {
          background: linear-gradient(135deg, #005DD4 0%, #0047a8 100%);
          color: #fff;
          padding: 16mm 18mm 14mm;
          text-align: center;
        }
        .brand {
          margin: 0 0 7px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.75);
        }
        .masthead h1 { margin: 0 0 12px; font-size: 21px; font-weight: 700 }
        .rule {
          width: 40px; height: 2px; margin: 0 auto;
          background: rgba(255,255,255,0.35);
        }

        .body { padding: 14mm 18mm 0; text-align: center }
        .lead {
          margin: 0 auto 10mm;
          max-width: 105mm;
          font-size: 13px;
          line-height: 1.6;
          color: #475569;
        }

        /* Quiet zone around the code is part of the spec, not padding for
           looks: scanners need the white border to find the symbol. */
        .code {
          width: 62mm; margin: 0 auto;
          padding: 7mm; background: #fff;
          border: 1px solid #e2e8f0; border-radius: 10px;
        }
        .code svg { display: block; width: 100%; height: auto }

        /* A real sequence, so it is numbered. */
        .steps {
          margin: 11mm auto 0; padding: 0; max-width: 105mm;
          list-style: none; text-align: left;
        }
        .steps li {
          position: relative;
          margin: 0 0 7px; padding-left: 26px;
          font-size: 12.5px; line-height: 1.55; color: #334155;
        }
        .steps li span {
          position: absolute; left: 0; top: 1px;
          width: 18px; height: 18px; border-radius: 50%;
          background: #eef2ff; color: #005DD4;
          font-size: 10.5px; font-weight: 700; line-height: 18px;
          text-align: center;
        }

        .notice {
          margin: 10mm auto 0; max-width: 105mm;
          padding: 4mm 5mm;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-left: 4px solid #f59e0b;
          border-radius: 0 8px 8px 0;
          font-size: 12px; line-height: 1.55; color: #374151;
          text-align: left;
        }

        .foot {
          margin: 12mm auto 0; max-width: 115mm;
          padding-top: 5mm; border-top: 1px solid #f1f5f9;
          font-size: 10.5px; line-height: 1.5; color: #94a3b8;
        }
      </style></head><body>
        <div class="sheet">
          <div class="masthead">
            <p class="brand">DeepEcho</p>
            <h1>${t('share.printTitle')}</h1>
            <div class="rule"></div>
          </div>
          <div class="body">
            <p class="lead">${t('share.printScan')}</p>
            <div class="code">${svg}</div>
            <ol class="steps">
              <li><span>1</span>${t('share.printStep1')}</li>
              <li><span>2</span>${t('share.printStep2')}</li>
              <li><span>3</span>${t('share.printStep3', { details: asked })}</li>
            </ol>
            <p class="notice">${t('share.printOnce')}</p>
            <p class="foot">${t('share.printFooter')}</p>
          </div>
        </div>
      </body></html>`
    );
    doc.close();

    // The SVG is inline markup with no external references, so there is nothing
    // to wait on before printing.
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Late enough for the print dialog to have taken its snapshot. Removing it
    // synchronously cancels the job in WebKit.
    window.setTimeout(() => frame.remove(), 1000);
  }, [t, minted]);

  const activeCount = useMemo(
    () => (shares ?? []).filter(s => s.is_active).length,
    [shares]
  );

  /** What the clinician should tell the patient. Reads off the minted share when
   *  there is one, so the created panel can never describe a different challenge
   *  from the one the link actually carries. */
  const challengeKey = minted?.challenge?.policy ?? recipient?.challenge?.policy ?? 'dob';

  // The house Modal already returns null when closed, and it unmounts its
  // children with it, so there is nothing to reset on close beyond what
  // handleClose does.
  return (
    <Modal isOpen={open} onClose={handleClose} size="xl">
      <Modal.Header
        title={t('share.title')}
        description={t('share.subtitle')}
        onClose={handleClose}
      />

      {minted ? (
        /* ================= the link, shown exactly once ================= */
        <>
          <Modal.Body className="flex flex-col gap-5">
            <div className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 dark:border-emerald-800 dark:bg-emerald-900/20">
              <LuCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                  {minted.delivery?.sent
                    ? t('share.sentTitle', {
                        where: minted.delivery.recipient ?? '',
                      })
                    : t('share.createdTitle')}
                </span>
                <span className="text-xs text-emerald-800 dark:text-emerald-300">
                  {t('share.expiresAt', {
                    when: formatWhen(minted.expires_at, locale),
                  })}
                </span>
              </div>
            </div>

            {/* The QR code is the point of this channel, so it leads. */}
            {minted.share.delivery_channel === 'qr_code' && (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
                {/* White stays white in dark mode: a scanner needs the contrast,
                    and an inverted code does not read. */}
                <div ref={qrRef} className="rounded-lg bg-white p-3">
                  <QRCode value={minted.url} size={168} level="M" />
                </div>
                <p className="max-w-xs text-center text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                  {t('share.qrHint')}
                </p>
                <Button variant="outline" onClick={handlePrint} className="gap-1.5">
                  <LuPrinter className="h-3.5 w-3.5" />
                  {t('share.print')}
                </Button>
              </div>
            )}

            {minted.delivery?.channel !== 'qr_code' && !minted.delivery?.sent && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-800 dark:bg-amber-900/20">
                <LuAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                  {t('share.notSent')}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <span className={sectionLabel}>{t('share.linkLabel')}</span>
              <div className="flex items-stretch gap-2">
                <Input
                  readOnly
                  value={minted.url}
                  onFocus={e => e.currentTarget.select()}
                  aria-label={t('share.linkLabel')}
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  onClick={() => void handleCopy()}
                  className="shrink-0 gap-1.5"
                >
                  {copied ? (
                    <LuCheck className="h-3.5 w-3.5" />
                  ) : (
                    <LuCopy className="h-3.5 w-3.5" />
                  )}
                  {copied ? t('share.copiedShort') : t('share.copy')}
                </Button>
              </div>
            </div>

            {/* What to say to her, before she walks out of the room. */}
            <div className="flex flex-col gap-2 rounded-lg bg-slate-50 px-3.5 py-3 dark:bg-gray-800/60">
              <span className={sectionLabel}>{t('share.tellHer')}</span>
              <ul className="flex flex-col gap-1.5 text-xs leading-relaxed text-slate-600 dark:text-gray-300">
                <li>
                  {t('share.tellHerAsked', {
                    details: t(`share.challengeNames.${challengeKey}`),
                  })}
                </li>
                <li>{t('share.tellHerOnce')}</li>
              </ul>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline" onClick={reset}>
              {t('share.createAnother')}
            </Button>
            <Button onClick={handleClose}>{t('share.done')}</Button>
          </Modal.Footer>
        </>
      ) : (
        /* ================= compose a new share ================= */
        <>
          <Modal.Body className="flex flex-col gap-5">
            {/* ---------- how it reaches her ---------- */}
            <div className="flex flex-col gap-2">
              <span className={sectionLabel}>{t('share.howToSend')}</span>
              <div className="grid grid-cols-3 gap-2">
                {CHANNELS.map(({ value, icon }) => {
                  const selected = channel === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChannel(value)}
                      aria-pressed={selected}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        selected
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/25 dark:text-blue-300'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      {icon}
                      <span className="text-xs font-medium leading-tight">
                        {t(`share.channel.${value}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                {t(`share.channelHint.${channel}`)}
              </p>
            </div>

            {/* ---------- where to send it ---------- */}
            {(needsPhone || needsEmail) && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="share-destination"
                  className={sectionLabel}
                >
                  {needsPhone ? t('share.phoneLabel') : t('share.emailLabel')}
                </label>
                <Input
                  id="share-destination"
                  type={needsPhone ? 'tel' : 'email'}
                  inputMode={needsPhone ? 'tel' : 'email'}
                  autoComplete="off"
                  value={needsPhone ? phone : email}
                  placeholder={
                    needsPhone
                      ? t('share.phonePlaceholder')
                      : t('share.emailPlaceholder')
                  }
                  aria-invalid={destinationInvalid}
                  onChange={e => {
                    setTouched(true);
                    if (needsPhone) {
                      setPhone(e.target.value);
                    } else {
                      setEmail(e.target.value);
                    }
                  }}
                  className={
                    destinationInvalid
                      ? 'border-red-400 focus-visible:ring-red-400 dark:border-red-500'
                      : ''
                  }
                />
                <span
                  className={`text-[11px] leading-snug ${
                    destinationInvalid
                      ? 'text-red-600 dark:text-red-400'
                      : onFile
                        ? 'text-slate-500 dark:text-gray-400'
                        : 'text-amber-700 dark:text-amber-400'
                  }`}
                >
                  {destinationInvalid
                    ? needsPhone
                      ? t('share.phoneInvalid')
                      : t('share.emailInvalid')
                    : recipientLoading
                      ? t('share.contactLoading')
                      : onFile
                        ? t('share.contactFromRecord')
                        : needsPhone
                          ? t('share.phoneMissing')
                          : t('share.emailMissing')}
                </span>
              </div>
            )}

            {/* ---------- what she sees ---------- */}
            <div className="flex flex-col gap-2">
              <span className={sectionLabel}>{t('share.whatToShare')}</span>
              <div className="flex flex-col gap-1.5">
                {SCOPES.map(value => {
                  const selected = scope === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setScope(value)}
                      aria-pressed={selected}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        selected
                          ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/25'
                          : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                          selected
                            ? 'border-blue-600 dark:border-blue-400'
                            : 'border-slate-300 dark:border-gray-600'
                        }`}
                      >
                        {selected && (
                          <span className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400" />
                        )}
                      </span>
                      <span className="text-sm text-slate-700 dark:text-gray-200">
                        {t(`share.scope.${value}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ---------- expiry, and what single use means ---------- */}
            <div className="flex flex-col gap-2">
              <label htmlFor="share-ttl" className={sectionLabel}>
                {t('share.expiresIn')}
              </label>
              <Select
                id="share-ttl"
                value={String(ttlHours)}
                onChange={e => setTtlHours(Number(e.target.value))}
                options={TTL_CHOICES.map(h => ({
                  value: String(h),
                  label: t('share.hours', { count: h }),
                }))}
              />
              {/* Suppressed rather than filled with a blank when the record
                  cannot be challenged: the warning below says what to fix, and
                  promising a question that does not exist is worse than silence. */}
              {!cannotChallenge && (
                <p className="text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                  {t('share.singleUseExplain', {
                    details: t(`share.challengeNames.${challengeKey}`),
                  })}
                </p>
              )}
            </div>

            {cannotChallenge && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-800 dark:bg-amber-900/20">
                <LuAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                  {t('share.cannotChallenge')}
                </p>
              </div>
            )}

            {/* ---------- existing links ---------- */}
            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-gray-700">
              <div className="flex items-baseline justify-between gap-3">
                <span className={sectionLabel}>{t('share.existingLinks')}</span>
                {activeCount > 0 && (
                  <span className="text-[11px] tabular-nums text-slate-500 dark:text-gray-400">
                    {t('share.activeCount', { count: activeCount })}
                  </span>
                )}
              </div>

              {sharesLoading ? (
                <p className="text-xs text-slate-400">{t('share.loading')}</p>
              ) : (shares ?? []).length === 0 ? (
                <p className="text-xs text-slate-400">{t('share.noneYet')}</p>
              ) : (
                <ul className="flex max-h-40 flex-col gap-1.5 overflow-y-auto">
                  {(shares ?? []).map(share => (
                    <li
                      key={share.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-gray-700"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-xs text-slate-700 dark:text-gray-200">
                          {t(`share.scopeShort.${share.content_scope}`)}
                          {' · '}
                          {t(`share.channel.${share.delivery_channel}`)}
                          {share.redaction_profile === 'anonymized' &&
                            ` · ${t('share.anonymizedTag')}`}
                        </span>
                        <span className="text-[11px] tabular-nums text-slate-400">
                          {t(shareStatusKey(share))} ·{' '}
                          {formatWhen(share.expires_at, locale)}
                        </span>
                      </div>
                      {share.is_active && (
                        <button
                          type="button"
                          onClick={() => revoke.mutate(share.id)}
                          disabled={revoke.isPending}
                          title={t('share.revoke')}
                          aria-label={t('share.revoke')}
                          className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20"
                        >
                          <LuX className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ---------- anonymize, last and quiet ----------

                Below everything else on purpose. The only use case here is the
                patient reading her own report, and she knows who she is, so this
                matters only for a link she might forward on. It stays available
                because that does happen, but it is not part of the normal flow. */}
            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-gray-700">
              <label
                htmlFor="share-anonymize"
                className="flex cursor-pointer items-start gap-2.5"
              >
                <Checkbox
                  id="share-anonymize"
                  checked={anonymize}
                  onChange={e => setAnonymize(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-slate-700 dark:text-gray-200">
                    {t('share.anonymizeLabel')}
                  </span>
                  <span className="text-xs leading-relaxed text-slate-500 dark:text-gray-400">
                    {t('share.anonymizeExplain')}
                  </span>
                </span>
              </label>

              {anonymize && (
                <div className="ml-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
                  <LuAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                    {t('share.anonymizeWarning')}
                  </p>
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline" onClick={handleClose}>
              {t('share.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={!canSubmit}>
              {create.isPending ? t('share.creating') : t('share.createLink')}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default ShareDialog;
